import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import images from "../../app/utlis/image";
import { getPageData, useData } from "../../app/helper/functions";
import { CLARITY_EVENTS } from "../../app/utlis/EventTypes";

const BOOK_FORM_ENDPOINT = process.env.REACT_APP_BOOK_FORM_ENDPOINT ?? "";
const CALENDLY_WIDGET_SCRIPT = "https://assets.calendly.com/assets/external/widget.js";

/* ───────── SEO via plain DOM ───────── */
const SEOHead = ({ seo }: { seo?: { title?: string; description?: string; canonical?: string } }) => {
  useEffect(() => {
    if (seo?.title) document.title = seo.title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    };

    window.scrollTo(0, 0);
    if (seo?.description) setMeta("description", seo.description);

    if (seo?.canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = seo.canonical;
    }
  }, [seo]);
  return null;
};

/* ───────── animation helpers ───────── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: "easeOut" },
  }),
};

/* ───────── component ───────── */
const BookPage = () => {
  const { data } = useData();
  const bookPageData = getPageData(data, "bookPage");

  const {
    seo,
    hero,
    whatWeCoverSection,
    trustSignals: rawTrustSignals,
    calendlySection,
    faqSection,
    formSection,
  } = bookPageData ?? {};

  const trustSignals: { text: string; highlight: boolean }[] = rawTrustSignals ?? [];
  const howFoundOptions: string[] = formSection?.howFoundOptions ?? [];

  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formState, setFormState] = useState({
    fullName: "",
    companyName: "",
    email: "",
    message: "",
    howFound: "",
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const calendlyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const existingScript = document.querySelector(
      `script[src="${CALENDLY_WIDGET_SCRIPT}"]`
    ) as HTMLScriptElement | null;

    const bookingUrl = calendlySection?.bookingUrl;

    const initCalendly = () => {
      const win = window as any;
      try {
        if (win?.Calendly && calendlyRef.current && bookingUrl) {
          // initialize inline widget into our container element
          win.Calendly.initInlineWidget({
            url: bookingUrl,
            parentElement: calendlyRef.current,
          });
        }
      } catch (e) {
        // ignore
      }
    };

    if (existingScript) {

      initCalendly();
      return;
    }

    const script = document.createElement("script");
    script.src = CALENDLY_WIDGET_SCRIPT;
    script.type = "text/javascript";
    script.async = true;
    script.onload = () => initCalendly();
    document.body.appendChild(script);
  }, [calendlySection?.bookingUrl]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!BOOK_FORM_ENDPOINT) {
      alert("Book form endpoint is not configured.");
      return;
    }
    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        source: "Compliance Call",
        name: formState.fullName,
        company: formState.companyName,
        email: formState.email,
        message: formState.message,
        discoverySource: formState.howFound,
      });
      await fetch(`${BOOK_FORM_ENDPOINT}?${params.toString()}`, {
        method: "GET",
        mode: "no-cors",
      });

      // ── Fire Clarity event on successful submission ──
      (window as any).clarity?.("event", CLARITY_EVENTS.BOOK_MESSAGE);

      setFormSubmitted(true);
    } catch {
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead seo={seo} />
      <div className="min-h-screen bg-[#0c0c0f] text-white">
        {/* ── Minimal Nav ── */}
        <nav className="flex items-center justify-between px-6 md:px-12 py-5 border-b border-white/10">
          <Link to="/">
            <img
              src={images.LOGO}
              alt="The Sattva Tech"
              className="w-40 bg-[#EDF1F5] p-2 rounded-lg"
            />
          </Link>
          <Link
            to="/"
            className="text-sm text-white/60 hover:text-white transition-colors"
          >
            ← Back to Home
          </Link>
        </nav>

        {/* ═══════════════ HERO ═══════════════ */}
        <section className="pt-16 pb-12 md:pt-24 md:pb-16 px-6 md:px-12 max-w-[960px] mx-auto text-center">
          <motion.span
            className="inline-block text-xs md:text-sm font-semibold tracking-widest uppercase text-[#4F8EF7] mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            {hero?.badge}
          </motion.span>

          <motion.h1
            className="text-3xl md:text-5xl font-bold leading-tight"
            variants={fadeUp}
            custom={0}
            initial="hidden"
            animate="visible"
          >
            {hero?.heading}
          </motion.h1>

          <motion.p
            className="mt-6 text-base md:text-lg text-white/80 max-w-3xl mx-auto leading-relaxed"
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="visible"
          >
            {hero?.subheading}
          </motion.p>
        </section>

        {/* ═══════════════ WHAT WE COVER ═══════════════ */}
        <section className="pb-16 px-6 md:px-12 max-w-[960px] mx-auto">
          <motion.h2
            className="text-2xl md:text-3xl font-semibold text-center mb-10"
            variants={fadeUp}
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {whatWeCoverSection?.title}
          </motion.h2>

          <div className="space-y-6">
            {whatWeCoverSection?.items?.map((item: { title: string; body: string }, i: number) => (
              <motion.div
                key={item.title}
                className="rounded-xl border border-white/10 bg-white/[0.03] px-6 py-5"
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <h3 className="text-lg md:text-xl font-semibold text-[#4F8EF7] mb-2">
                  → {item.title}
                </h3>
                <p className="text-sm md:text-base text-white/75 leading-relaxed">
                  {item.body}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ═══════════════ TRUST SIGNALS ═══════════════ */}
        <section className="pb-16 px-6 md:px-12 max-w-[960px] mx-auto">
          <div className="flex flex-col md:flex-row gap-4 md:gap-6 justify-center">
            {trustSignals.map((item, i) => (
              <motion.div
                key={i}
                className={`relative overflow-hidden flex items-start gap-3 rounded-lg px-5 py-4 flex-1 ${item.highlight
                    ? "border border-[#4F8EF7]/30 bg-gradient-to-br from-[#4F8EF7]/12 via-white/[0.03] to-white/[0.02]"
                    : "border border-white/10 bg-white/[0.03]"
                  }`}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                whileHover={{ y: -4 }}
                viewport={{ once: true }}
              >
                {item.highlight && (
                  <>
                    <motion.span
                      className="pointer-events-none absolute inset-0 rounded-lg"
                      animate={{
                        boxShadow: [
                          "0 0 0 rgba(79, 142, 247, 0.14)",
                          "0 0 28px rgba(79, 142, 247, 0.32)",
                          "0 0 0 rgba(79, 142, 247, 0.14)",
                        ],
                      }}
                      transition={{
                        duration: 2.6,
                        repeat: Infinity,
                        ease: "easeInOut",
                        delay: i * 0.22,
                      }}
                    />
                    <motion.span
                      className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      animate={{ x: ["-20%", "360%"] }}
                      transition={{
                        duration: 2.9,
                        repeat: Infinity,
                        repeatDelay: 1,
                        ease: "easeInOut",
                        delay: i * 0.28,
                      }}
                    />
                  </>
                )}
                <span className="text-green-400 text-lg leading-none mt-0.5">✓</span>
                <span
                  className={`relative text-sm md:text-base leading-relaxed ${item.highlight ? "text-white font-medium" : "text-white/80"
                    }`}
                >
                  {item.text}
                </span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ═══════════════ CALENDLY EMBED ═══════════════ */}
        <section className="pb-16 px-6 md:px-12 max-w-[960px] mx-auto text-center">
          <motion.h2
            className="text-2xl md:text-3xl font-semibold mb-8"
            variants={fadeUp}
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {calendlySection?.title}
          </motion.h2>

          <div className="w-full rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden min-h-[650px]">
            <div
              className="calendly-inline-widget"
              data-url={calendlySection?.bookingUrl}
              style={{ minWidth: 320, height: 700 }}
            />
          </div>
        </section>

        {/* ═══════════════ FAQ ═══════════════ */}
        <section className="pb-16 px-6 md:px-12 max-w-[960px] mx-auto">
          <motion.h2
            className="text-2xl md:text-3xl font-semibold text-center mb-10"
            variants={fadeUp}
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {faqSection?.title}
          </motion.h2>

          <div className="space-y-3">
            {faqSection?.items?.map((faq: { q: string; a: string }, i: number) => (
              <motion.div
                key={i}
                className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="text-sm md:text-base font-medium text-white/90">
                    {faq.q}
                  </span>
                  <span
                    className={`ml-4 text-white/50 transition-transform duration-300 ${openFaq === i ? "rotate-45" : ""
                      }`}
                  >
                    +
                  </span>
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pb-5 text-sm md:text-base text-white/65 leading-relaxed">
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        {/* ═══════════════ FALLBACK FORM ═══════════════ */}
        <section className="pb-20 px-6 md:px-12 max-w-[720px] mx-auto">
          <motion.h2
            className="text-2xl md:text-3xl font-semibold text-center mb-3"
            variants={fadeUp}
            custom={0}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {formSection?.title}
          </motion.h2>
          <p className="text-center text-white/60 text-sm md:text-base mb-10">
            {formSection?.subtext}
          </p>

          {formSubmitted ? (
            <div className="text-center py-16">
              <p className="text-xl font-semibold text-[#4F8EF7]">
                {formSection?.successTitle}
              </p>
              <p className="mt-2 text-white/60 text-sm">
                {formSection?.successBody}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input
                  type="text"
                  name="fullName"
                  required
                  placeholder="Full Name"
                  value={formState.fullName}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-[#4F8EF7]/60 transition-colors"
                />
                <input
                  type="text"
                  name="companyName"
                  required
                  placeholder="Company Name"
                  value={formState.companyName}
                  onChange={handleChange}
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-[#4F8EF7]/60 transition-colors"
                />
              </div>
              <input
                type="email"
                name="email"
                required
                placeholder="Email Address"
                value={formState.email}
                onChange={handleChange}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-[#4F8EF7]/60 transition-colors"
              />
              <textarea
                name="message"
                required
                rows={3}
                placeholder="What you're working on"
                value={formState.message}
                onChange={handleChange}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-[#4F8EF7]/60 transition-colors resize-none"
              />
              <select
                name="howFound"
                required
                value={formState.howFound}
                onChange={handleChange}
                className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[#4F8EF7]/60 transition-colors appearance-none"
              >
                <option value="" disabled>
                  How did you find us?
                </option>
                {howFoundOptions.map((opt) => (
                  <option key={opt} value={opt} className="bg-[#1a1a1a]">
                    {opt}
                  </option>
                ))}
              </select>

              <button
                type="submit"
                disabled={submitting}
                className="w-full md:w-auto px-10 py-3 rounded-lg bg-[#4F8EF7] text-white font-semibold text-sm hover:bg-[#3b7be0] transition-colors disabled:opacity-50"
              >
                {submitting ? formSection?.submitButtonLoadingText : formSection?.submitButtonText}
              </button>
            </form>
          )}

          <p className="mt-4 text-xs text-white/40 text-center">
            {formSection?.footerNote}
          </p>
        </section>
      </div>
    </>
  );
};

export default BookPage;
